import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapView.css';

// Coordenadas de Lorena, SP
const LORENA_CENTER = [-22.7306, -45.1247];

// Fix para ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

/** Verde no mapa = MK mandando dados para a API (sinal recente). WAN = status_internet. */
const isAntenaOnline = (eq) =>
  eq.status_antena_online !== undefined && eq.status_antena_online !== null
    ? Boolean(eq.status_antena_online)
    : Boolean(Number(eq.status_internet));

const MapAutoResize = ({ layoutKey }) => {
  const map = useMap();

  useEffect(() => {
    // Leaflet pode renderizar “em branco” quando o container muda de tamanho
    // (ex.: abrir/fechar sidebar). Invalidate em ticks diferentes para pegar transição CSS.
    const t1 = setTimeout(() => map.invalidateSize(), 0);
    const t2 = setTimeout(() => map.invalidateSize(), 350);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map, layoutKey]);

  return null;
};

const MapView = ({ equipamentos, loading, onNovoCadastro, layoutKey }) => {
  // Criar ícones customizados
  const greenIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  const redIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  const formatDateOnly = (value) => {
    if (!value) return 'N/D';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      const parts = String(value).split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return 'N/D';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Filtrar equipamentos com coordenadas válidas
  const equipamentosComCoordenadas = equipamentos
    ? equipamentos.filter(eq => {
        const lat = parseFloat(eq.latitude);
        const lng = parseFloat(eq.longitude);
        return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
      })
    : [];

  return (
    <div className="map-view">
      <div className="map-header">
        <div>
          <h1>Mapa de Lorena</h1>
          <p>Monitoramento de Equipamentos - {equipamentosComCoordenadas.length} equipamento(s) no mapa</p>
        </div>
        <button 
          className="novo-cadastro-btn" 
          onClick={onNovoCadastro || (() => {})}
          title="Cadastrar novo equipamento"
        >
          <span>+</span> Novo Cadastro
        </button>
      </div>
      
      {loading ? (
        <div className="loading">Carregando mapa...</div>
      ) : (
        <div className="map-canvas">
          <MapContainer
            center={LORENA_CENTER}
            zoom={13}
            className="leaflet-map"
            style={{ height: '100%', width: '100%' }}
          >
            <MapAutoResize layoutKey={layoutKey} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          {equipamentosComCoordenadas.length > 0 ? (
            equipamentosComCoordenadas.map((equipamento) => {
              const lat = parseFloat(equipamento.latitude);
              const lng = parseFloat(equipamento.longitude);
              const position = [lat, lng];
              const radius = 20; // 20 metros
              
              const isOnline = isAntenaOnline(equipamento);
              const wanAtiva =
                Number(equipamento.status_internet) === 1 || equipamento.status_internet === true;

              return (
                <React.Fragment key={equipamento.id}>
                  {/* Círculo de 20 metros de raio */}
                  <Circle
                    center={position}
                    radius={radius}
                    pathOptions={{
                      color: isOnline ? '#28a745' : '#dc3545',
                      fillColor: isOnline ? '#28a745' : '#dc3545',
                      fillOpacity: 0.2,
                      weight: 2,
                      opacity: 0.6
                    }}
                  />
                  {/* Marcador */}
                  <Marker
                    position={position}
                    icon={isOnline ? greenIcon : redIcon}
                  >
                    <Popup>
                      <div className="popup-content">
                        <h3>{equipamento.descricao_mk || 'Equipamento'}</h3>
                        <p><strong>IP:</strong> {equipamento.ip}</p>
                        {equipamento.server_name && (
                          <p><strong>Servidor cadastrado:</strong> {equipamento.server_name}</p>
                        )}
                        {equipamento.mk_ultima_identity && (
                          <p><strong>Identity recebida:</strong> {equipamento.mk_ultima_identity}</p>
                        )}
                        <p><strong>Endereço:</strong> {equipamento.endereco}{equipamento.numero ? `, ${equipamento.numero}` : ''}</p>
                        {equipamento.bairro && <p><strong>Bairro:</strong> {equipamento.bairro}</p>}
                        {equipamento.patrimonio && <p><strong>Patrimônio:</strong> {equipamento.patrimonio}</p>}
                        <p>
                          <strong>Sinal na API:</strong>{' '}
                          <span className={isOnline ? 'status-online' : 'status-offline'}>
                            {isOnline ? 'Sim' : 'Não'}
                          </span>
                        </p>
                        <p>
                          <strong>WAN (script):</strong>{' '}
                          <span className={wanAtiva ? 'status-online' : 'status-offline'}>
                            {wanAtiva ? 'Ativo' : 'Inativo'}
                          </span>
                        </p>
                        {equipamento.ultima_verificacao && (
                          <p><strong>Última verificação:</strong> {formatDateOnly(equipamento.ultima_verificacao)}</p>
                        )}
                        {equipamento.ultima_verificacao_ok && (
                          <p><strong>Último sinal OK:</strong> {formatDateOnly(equipamento.ultima_verificacao_ok)}</p>
                        )}
                        <p><strong>Raio de cobertura:</strong> 20 metros</p>
                        {equipamento.velocidade_download !== undefined &&
                          equipamento.velocidade_download !== null &&
                          String(equipamento.velocidade_download).trim() !== '' && (
                            <p>
                              <strong>Download:</strong>{' '}
                              {typeof equipamento.velocidade_download === 'number'
                                ? equipamento.velocidade_download.toFixed(2)
                                : Number.parseFloat(equipamento.velocidade_download).toFixed(2)}{' '}
                              Mbps
                            </p>
                          )}
                        {equipamento.velocidade_upload !== undefined &&
                          equipamento.velocidade_upload !== null &&
                          String(equipamento.velocidade_upload).trim() !== '' && (
                            <p>
                              <strong>Upload:</strong>{' '}
                              {typeof equipamento.velocidade_upload === 'number'
                                ? equipamento.velocidade_upload.toFixed(2)
                                : Number.parseFloat(equipamento.velocidade_upload).toFixed(2)}{' '}
                              Mbps
                            </p>
                          )}
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })
          ) : (
            !loading && (
              <div style={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                background: 'white',
                padding: '20px',
                borderRadius: '10px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                zIndex: 1000,
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '1.1em', fontWeight: '600' }}>
                  {equipamentos && equipamentos.length > 0 
                    ? 'Nenhum equipamento com coordenadas cadastrado' 
                    : 'Nenhum equipamento cadastrado'}
                </p>
                <p style={{ fontSize: '0.9em', color: '#666', margin: '0' }}>
                  {equipamentos && equipamentos.length > 0
                    ? 'Cadastre equipamentos com latitude e longitude para visualizá-los no mapa'
                    : 'Cadastre equipamentos para visualizá-los no mapa'}
                </p>
                {onNovoCadastro && (
                  <button
                    onClick={onNovoCadastro}
                    style={{
                      marginTop: '15px',
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      fontWeight: '600'
                    }}
                  >
                    + Cadastrar Equipamento
                  </button>
                )}
              </div>
            )
          )}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default MapView;
